import React, { useState, useEffect } from 'react';
import { getHrisCuti, getHrisCutiById, createHrisCuti, updateHrisCuti, deleteHrisCuti, approveHrisCuti, getHrisSisaCuti, getKaryawan } from '../../api';
import { Plus, Edit2, Trash2, Calendar, CheckCircle, XCircle, Search, Clock, CheckSquare, Eye, FileText, BarChart3, DollarSign } from 'lucide-react';

const JENIS_CUTI = ['Tahunan', 'Besar', 'Izin', 'Sakit', 'Khusus', 'Melahirkan'];
const STATUS_CUTI = ['Menunggu', 'Disetujui', 'Ditolak', 'Dibatalkan'];

const statusConfig = {
  'Menunggu': { bg: '#fef3c7', color: '#92400e', dot: '#fb6340' },
  'Disetujui': { bg: '#d1fae5', color: '#065f46', dot: '#2dce89' },
  'Ditolak': { bg: '#fee2e2', color: '#991b1b', dot: '#f5365c' },
  'Dibatalkan': { bg: '#f3f4f6', color: '#6b7280', dot: '#8898aa' },
};

const TAB_STYLE = {
  active: {
    borderBottom: '2px solid var(--cyan-500)',
    color: 'var(--cyan-600)',
    fontWeight: 600,
    background: 'rgba(6,182,212,0.06)',
  },
  inactive: {
    borderBottom: '2px solid transparent',
    color: 'var(--gray-400)',
    fontWeight: 500,
    background: 'transparent',
  }
};

export default function HrisCuti() {
  const [activeTab, setActiveTab] = useState('rekap');
  const [data, setData] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [sisaCuti, setSisaCuti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTahun, setFilterTahun] = useState(new Date().getFullYear());
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showApprove, setShowApprove] = useState(null);
  const [form, setForm] = useState({
    karyawan_id: '', jenis_cuti: 'Tahunan', tgl_mulai: '', tgl_selesai: '',
    total_hari: 1, ganti_libur: 0, alasan: ''
  });
  const [detailItem, setDetailItem] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchData = () => {
    setLoading(true);
    Promise.all([getHrisCuti(), getKaryawan(), getHrisSisaCuti()])
      .then(([dRes, kRes, sRes]) => { setData(dRes.data); setKaryawan(kRes.data); setSisaCuti(sRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  // === Computed values ===
  const totalData = data.length;
  const totalMenunggu = data.filter(d => d.status === 'Menunggu').length;
  const totalDisetujui = data.filter(d => d.status === 'Disetujui').length;
  const totalDitolak = data.filter(d => d.status === 'Ditolak' || d.status === 'Dibatalkan').length;

  // === Rekap Cuti data ===
  const rekapData = sisaCuti.filter(s => s.tahun === filterTahun);

  // Helper: get karyawan by id
  const getKaryawanById = (id) => karyawan.find(k => k.id === id) || {};
  const getNamaNik = (karyawanId) => {
    const k = getKaryawanById(karyawanId);
    return k ? `${k.nama || '-'} - ${k.nik || '-'}` : '-';
  };
  const getJabatan = (karyawanId) => {
    const k = getKaryawanById(karyawanId);
    return k?.jabatan || '-';
  };

  // === Pengajuan Cuti filtered ===
  const filteredCuti = data.filter(d => {
    const matchSearch = !search || (d.nama || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || d.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // === CRUD Handlers ===
  const openCreate = () => {
    setEditing(null);
    setForm({ karyawan_id: '', jenis_cuti: 'Tahunan', tgl_mulai: '', tgl_selesai: '', total_hari: 1, ganti_libur: 0, alasan: '' });
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await getHrisCutiById(id);
      const d = res.data;
      if (d.status !== 'Menunggu') { alert('Hanya cuti dengan status Menunggu yang bisa diedit'); return; }
      setEditing(d);
      setForm({
        karyawan_id: d.karyawan_id, jenis_cuti: d.jenis_cuti, tgl_mulai: d.tgl_mulai,
        tgl_selesai: d.tgl_selesai, total_hari: d.total_hari || d.total_akhir,
        ganti_libur: d.ganti_libur || 0, alasan: d.alasan,
      });
      setShowModal(true);
    } catch (e) { alert('Gagal memuat data'); }
  };

  const handleSubmit = async () => {
    if (!form.karyawan_id || !form.tgl_mulai || !form.jenis_cuti) { alert('Lengkapi semua field'); return; }
    const payload = {
      ...form,
      total_akhir: form.total_hari - form.ganti_libur,
    };
    try {
      if (editing) {
        await updateHrisCuti(editing.id, payload);
      } else {
        await createHrisCuti(payload);
      }
      setShowModal(false);
      fetchData();
    } catch (e) { alert('Gagal menyimpan: ' + (e.response?.data?.error || e.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pengajuan cuti ini?')) return;
    try { await deleteHrisCuti(id); fetchData(); } catch (e) { alert('Gagal menghapus'); }
  };

  const handleApprove = async () => {
    if (!showApprove) return;
    try {
      await approveHrisCuti(showApprove.id, { status: showApprove.action, catatan_atasan: showApprove.catatan || '' });
      setShowApprove(null);
      fetchData();
    } catch (e) { alert('Gagal memproses: ' + (e.response?.data?.error || e.message)); }
  };

  // Get sisa cuti display
  const getSisaCutiDisplay = (karyawanId) => {
    const sc = sisaCuti.find(s => s.karyawan_id === karyawanId && s.tahun === filterTahun);
    return sc ? `${sc.sisa_cuti}/${sc.jatah_cuti}` : '-';
  };

  // === Tabel Styling ===
  const tableStyle = {
    width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px',
    fontSize: '0.82rem',
  };
  const thStyle = {
    textAlign: 'left', padding: '12px 14px', fontWeight: 600, fontSize: '0.72rem',
    textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)',
    borderBottom: '2px solid var(--gray-100)', whiteSpace: 'nowrap',
  };
  const tdStyle = {
    padding: '10px 14px', background: '#fff', borderBottom: '1px solid var(--gray-50)',
    color: 'var(--gray-600)', verticalAlign: 'middle',
  };
  const rowHover = { background: 'var(--accent-tint-bg)' };

  if (loading) return <div className="page-loading"><div className="spinner-lg" /></div>;

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="page-header" style={{ borderBottom: '2px solid var(--gray-100)', paddingBottom: 16, marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          <Calendar size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: 'var(--cyan-500)' }} />
          Data Cuti & Izin
        </h2>
      </div>

      {/* Stat Cards */}
      {data.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid var(--info)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Total Pengajuan</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gray-600)', lineHeight: 1.2 }}>{totalData}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #fb6340' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Menunggu</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#92400e', lineHeight: 1.2 }}>{totalMenunggu}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #2dce89' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Disetujui</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#065f46', lineHeight: 1.2 }}>{totalDisetujui}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #f5365c' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Ditolak/Dibatalkan</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#991b1b', lineHeight: 1.2 }}>{totalDitolak}</div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{
        display: 'flex', gap: 0, marginBottom: 0,
        borderBottom: '2px solid var(--gray-100)',
        background: '#fff', borderRadius: '12px 12px 0 0',
        boxShadow: '0 0 2rem 0 rgba(136,152,170,0.08)',
      }}>
        <button
          onClick={() => { setActiveTab('rekap'); setSearch(''); }}
          style={{
            flex: 1, padding: '14px 20px', border: 'none', cursor: 'pointer',
            fontSize: '0.82rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'all 0.2s',
            ...(activeTab === 'rekap' ? TAB_STYLE.active : TAB_STYLE.inactive),
          }}>
          <BarChart3 size={17} />
          Rekap Cuti Tahunan
          {rekapData.length > 0 && (
            <span style={{
              fontSize: '0.65rem', padding: '2px 7px', borderRadius: 10,
              background: activeTab === 'rekap' ? 'var(--cyan-500)' : 'var(--gray-200)',
              color: '#fff', fontWeight: 600, marginLeft: 4,
            }}>{rekapData.length}</span>
          )}
        </button>
        <button
          onClick={() => { setActiveTab('pengajuan'); }}
          style={{
            flex: 1, padding: '14px 20px', border: 'none', cursor: 'pointer',
            fontSize: '0.82rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            transition: 'all 0.2s',
            ...(activeTab === 'pengajuan' ? TAB_STYLE.active : TAB_STYLE.inactive),
          }}>
          <FileText size={17} />
          Pengajuan Cuti
          {filteredCuti.length > 0 && (
            <span style={{
              fontSize: '0.65rem', padding: '2px 7px', borderRadius: 10,
              background: activeTab === 'pengajuan' ? 'var(--cyan-500)' : 'var(--gray-200)',
              color: '#fff', fontWeight: 600, marginLeft: 4,
            }}>{filteredCuti.length}</span>
          )}
        </button>
      </div>

      {/* ============ TAB 1: REKAP CUTI TAHUNAN ============ */}
      {activeTab === 'rekap' && (
        <>
          {/* Toolbar Rekap */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px', background: '#fff', borderRadius: '0 0 12px 12px',
            boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)',
            flexWrap: 'wrap', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Calendar size={16} style={{ color: 'var(--gray-400)' }} />
              <label style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Tahun:</label>
              <select value={filterTahun} onChange={e => setFilterTahun(parseInt(e.target.value))}
                className="form-select"
                style={{ width: 100, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', fontSize: '0.82rem' }}>
                {[2024, 2025, 2026, 2027].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div style={{ marginLeft: 'auto', fontSize: '0.8rem', color: 'var(--gray-400)' }}>
              Total <strong style={{ color: 'var(--gray-600)' }}>{rekapData.length}</strong> karyawan
            </div>
          </div>

          {/* Table Rekap Cuti Tahunan */}
          {rekapData.length === 0 ? (
            <div className="page-loading">Belum ada data sisa cuti untuk tahun {filterTahun}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: 50, textAlign: 'center' }}>No</th>
                    <th style={thStyle}>Nama - NIK</th>
                    <th style={thStyle}>Jabatan</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Hak Cuti Tahunan</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Cuti Terpakai</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Sisa Cuti</th>
                  </tr>
                </thead>
                <tbody>
                  {rekapData.map((s, idx) => {
                    const sisaPct = s.jatah_cuti > 0 ? (s.sisa_cuti / s.jatah_cuti) * 100 : 0;
                    const barColor = sisaPct >= 50 ? '#2dce89' : sisaPct >= 25 ? '#fb6340' : '#f5365c';
                    return (
                      <tr key={s.id} style={{ transition: 'all 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = rowHover.background}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--gray-400)' }}>{idx + 1}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: 8,
                              background: 'linear-gradient(135deg, var(--cyan-500), var(--cyan-700))',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                            }}>
                              {(s.nama || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--gray-600)' }}>
                                {getNamaNik(s.karyawan_id).split(' - ')[0]}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                                NIK: {getNamaNik(s.karyawan_id).split(' - ')[1] || '-'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={tdStyle}>{getJabatan(s.karyawan_id)}</td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: 'var(--gray-600)' }}>{s.jatah_cuti} hari</td>
                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600, color: s.cuti_terpakai > 0 ? '#fb6340' : 'var(--gray-400)' }}>{s.cuti_terpakai} hari</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                            <span style={{
                              fontWeight: 700, fontSize: '0.9rem',
                              color: s.sisa_cuti <= 0 ? '#f5365c' : s.sisa_cuti <= 3 ? '#fb6340' : '#065f46',
                            }}>{s.sisa_cuti} hari</span>
                            <div style={{
                              width: 60, height: 6, background: 'var(--gray-100)',
                              borderRadius: 3, overflow: 'hidden',
                            }}>
                              <div style={{
                                width: `${Math.min(100, sisaPct)}%`, height: '100%',
                                borderRadius: 3, background: barColor,
                                transition: 'width 0.5s',
                              }} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ============ TAB 2: PENGAJUAN CUTI ============ */}
      {activeTab === 'pengajuan' && (
        <>
          {/* Toolbar Pengajuan Cuti */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '12px 16px', background: '#fff', borderRadius: '0 0 12px 12px',
            boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)',
            flexWrap: 'wrap', marginBottom: 20,
          }}>
            <button className="btn btn-primary" onClick={openCreate} style={{ whiteSpace: 'nowrap' }}>
              <Plus size={16} /> Ajukan Cuti
            </button>
            <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
              <div className="search-box" style={{ width: '100%', margin: 0 }}>
                <Search size={16} />
                <input placeholder="Cari berdasarkan nama..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
              <label style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Status:</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="form-select"
                style={{ width: 'auto', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', fontSize: '0.82rem' }}>
                <option value="">Semua</option>
                {STATUS_CUTI.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
                <strong style={{ color: 'var(--gray-600)' }}>{filteredCuti.length}</strong>/{totalData}
              </span>
            </div>
          </div>

          {/* Table Pengajuan Cuti */}
          {filteredCuti.length === 0 ? (
            <div className="page-loading">Tidak ada data pengajuan cuti</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: 40, textAlign: 'center' }}>No</th>
                    <th style={thStyle}>Nama</th>
                    <th style={thStyle}>Tgl Mulai</th>
                    <th style={thStyle}>Tgl Selesai</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Jumlah Hari</th>
                    <th style={thStyle}>Keterangan</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Ganti Libur</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Total Akhir</th>
                    <th style={{ ...thStyle, width: 80, textAlign: 'center' }}>Status</th>
                    <th style={{ ...thStyle, width: 120, textAlign: 'center' }}>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCuti.map((d, idx) => {
                    const sc = statusConfig[d.status] || statusConfig['Menunggu'];
                    const totalAkhir = d.total_akhir || (d.total_hari - (d.ganti_libur || 0));
                    return (
                      <React.Fragment key={d.id}>
                        <tr style={{ transition: 'all 0.15s', cursor: 'pointer' }}
                          onClick={() => setExpandedRow(expandedRow === d.id ? null : d.id)}
                          onMouseEnter={e => e.currentTarget.style.background = rowHover.background}
                          onMouseLeave={e => {
                            if (expandedRow !== d.id) e.currentTarget.style.background = 'transparent';
                          }}>
                          <td style={{ ...tdStyle, textAlign: 'center', color: 'var(--gray-400)' }}>{idx + 1}</td>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{d.nama || '-'}</td>
                          <td style={tdStyle}>{d.tgl_mulai}</td>
                          <td style={tdStyle}>{d.tgl_selesai}</td>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700 }}>{d.total_hari} hari</td>
                          <td style={{ ...tdStyle, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.alasan || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>-</span>}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {d.ganti_libur > 0 ? (
                              <span style={{ color: '#fb6340', fontWeight: 600 }}>{d.ganti_libur} hari</span>
                            ) : (
                              <span style={{ color: 'var(--gray-400)' }}>-</span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: 'var(--cyan-700)' }}>
                            {totalAkhir} hari
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                              background: sc.bg, color: sc.color, whiteSpace: 'nowrap',
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot }} />
                              {d.status}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                              {d.status === 'Menunggu' && (
                                <>
                                  <button className="btn-icon" title="Setujui"
                                    onClick={(e) => { e.stopPropagation(); setShowApprove({ id: d.id, action: 'Disetujui', catatan: '' }); }}
                                    style={{ color: '#2dce89', width: 28, height: 28 }}>
                                    <CheckCircle size={14} />
                                  </button>
                                  <button className="btn-icon" title="Tolak"
                                    onClick={(e) => { e.stopPropagation(); setShowApprove({ id: d.id, action: 'Ditolak', catatan: '' }); }}
                                    style={{ color: '#f5365c', width: 28, height: 28 }}>
                                    <XCircle size={14} />
                                  </button>
                                  <button className="btn-icon" title="Edit"
                                    onClick={(e) => { e.stopPropagation(); openEdit(d.id); }}
                                    style={{ width: 28, height: 28 }}>
                                    <Edit2 size={13} />
                                  </button>
                                  <button className="btn-icon btn-danger" title="Hapus"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }}
                                    style={{ width: 28, height: 28 }}>
                                    <Trash2 size={13} />
                                  </button>
                                </>
                              )}
                              {d.status !== 'Menunggu' && (
                                <button className="btn-icon" title="Detail"
                                  onClick={(e) => { e.stopPropagation(); setDetailItem(d); }}
                                  style={{ color: 'var(--cyan-500)', width: 28, height: 28 }}>
                                  <Eye size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {/* Expanded Row - Detail */}
                        {expandedRow === d.id && (
                          <tr>
                            <td colSpan={10} style={{ padding: 0, background: '#f8f9fe' }}>
                              <div style={{
                                padding: '14px 20px', borderBottom: '1px solid var(--gray-100)',
                                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14,
                                fontSize: '0.8rem',
                              }}>
                                <div>
                                  <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 4 }}>Jenis Cuti</div>
                                  <div style={{ color: 'var(--gray-600)', fontWeight: 600 }}>{d.jenis_cuti}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 4 }}>Total Hari</div>
                                  <div style={{ color: 'var(--gray-600)', fontWeight: 600 }}>{d.total_hari} hari</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 4 }}>Ganti Libur</div>
                                  <div style={{ color: '#fb6340', fontWeight: 600 }}>{d.ganti_libur || 0} hari</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 4 }}>Total Akhir</div>
                                  <div style={{ color: 'var(--cyan-700)', fontWeight: 700, fontSize: '0.9rem' }}>{totalAkhir} hari</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 4 }}>Sisa Cuti</div>
                                  <div style={{ color: 'var(--gray-600)', fontWeight: 600 }}>{getSisaCutiDisplay(d.karyawan_id)}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 4 }}>Alasan</div>
                                  <div style={{ color: 'var(--gray-500)', fontStyle: 'italic' }}>{d.alasan || '-'}</div>
                                </div>
                                {d.status !== 'Menunggu' && d.approved_name && (
                                  <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--gray-200)', paddingTop: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <CheckSquare size={14} style={{ color: d.status === 'Disetujui' ? '#2dce89' : '#f5365c' }} />
                                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                                        {d.status === 'Disetujui' ? 'Disetujui' : 'Ditolak'} oleh <strong>{d.approved_name}</strong>
                                        {d.catatan_atasan && ` — ${d.catatan_atasan}`}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ============ FORM MODAL (Create/Edit) ============ */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, var(--cyan-500) 0%, var(--cyan-700) 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Calendar size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                {editing ? 'Edit Cuti' : 'Ajukan Cuti'}
              </h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)',
                border: '1px solid var(--cyan-100)', overflow: 'hidden',
              }}>
                {/* Section Header Informasi Cuti */}
                <div style={{
                  padding: '12px 16px', borderBottom: '1px solid var(--cyan-100)',
                  background: 'rgba(255,255,255,0.6)',
                  fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.5px', color: 'var(--cyan-700)',
                  textAlign: 'center',
                }}>
                  <Calendar size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Informasi Cuti
                </div>

                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Karyawan <span style={{color:'var(--danger)'}}>*</span></label>
                    <select value={form.karyawan_id} onChange={e => setForm({...form, karyawan_id: parseInt(e.target.value)})} required>
                      <option value="">Pilih Karyawan</option>
                      {karyawan.map(k => <option key={k.id} value={k.id}>{k.nama} (Sisa cuti: {getSisaCutiDisplay(k.id)})</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Jenis Cuti <span style={{color:'var(--danger)'}}>*</span></label>
                    <select value={form.jenis_cuti} onChange={e => setForm({...form, jenis_cuti: e.target.value})}>
                      {JENIS_CUTI.map(j => <option key={j} value={j}>{j}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Tanggal Mulai <span style={{color:'var(--danger)'}}>*</span></label>
                    <input type="date" value={form.tgl_mulai} onChange={e => setForm({...form, tgl_mulai: e.target.value})} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Tanggal Selesai</label>
                    <input type="date" value={form.tgl_selesai} onChange={e => {
                      setForm({...form, tgl_selesai: e.target.value});
                      if (form.tgl_mulai && e.target.value) {
                        const ms = new Date(e.target.value) - new Date(form.tgl_mulai);
                        const days = Math.round(ms / (1000*60*60*24)) + 1;
                        setForm(prev => ({...prev, tgl_selesai: e.target.value, total_hari: days > 0 ? days : 1}));
                      }
                    }} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Jumlah Hari</label>
                    <input type="number" min={1} value={form.total_hari}
                      onChange={e => setForm({...form, total_hari: parseInt(e.target.value) || 1})} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Ganti Libur (hari)</label>
                    <input type="number" min={0} max={form.total_hari} value={form.ganti_libur}
                      onChange={e => {
                        const val = parseInt(e.target.value) || 0;
                        const max = form.total_hari || 1;
                        setForm({...form, ganti_libur: Math.min(val, max)});
                      }}
                      placeholder="Hari libur pengganti" />
                    {form.ganti_libur > 0 && (
                      <div style={{ fontSize: '0.72rem', color: 'var(--cyan-600)', marginTop: 4, fontWeight: 500 }}>
                        Total Akhir: <strong>{form.total_hari - form.ganti_libur} hari</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* Alasan - full width */}
                <div style={{ padding: '0 16px 14px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Alasan <span style={{color:'var(--danger)'}}>*</span></label>
                    <textarea value={form.alasan} onChange={e => setForm({...form, alasan: e.target.value})} rows={2} required
                      style={{ fontSize: '0.82rem' }} />
                  </div>
                </div>

                {/* Summary Bar */}
                {form.ganti_libur > 0 && (
                  <div style={{
                    borderTop: '1px dashed var(--cyan-200)', background: 'rgba(255,255,255,0.4)',
                    padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    fontSize: '0.82rem',
                  }}>
                    <span style={{ color: 'var(--gray-500)' }}>
                      <Clock size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {form.total_hari} hari - {form.ganti_libur} hari ganti libur
                    </span>
                    <span style={{ fontWeight: 700, color: 'var(--cyan-700)' }}>
                      Total Akhir: {form.total_hari - form.ganti_libur} hari
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSubmit}>{editing ? 'Simpan' : 'Ajukan'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ APPROVE/REJECT MODAL ============ */}
      {showApprove && (
        <div className="modal-overlay" onClick={() => setShowApprove(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: showApprove.action === 'Disetujui'
                ? 'linear-gradient(135deg, #2dce89, #1aae6f)'
                : 'linear-gradient(135deg, #f5365c, #c0392b)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {showApprove.action === 'Disetujui' ? <CheckCircle size={18} color="white" /> : <XCircle size={18} color="white" />}
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                {showApprove.action === 'Disetujui' ? 'Setujui Cuti' : 'Tolak Cuti'}
              </h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: showApprove.action === 'Disetujui' ? '#f0fdf4' : '#fff5f5',
                borderRadius: 'var(--radius)', padding: 16,
                border: `1px solid ${showApprove.action === 'Disetujui' ? '#bbf7d0' : '#fecaca'}`
              }}>
                <div className="form-group">
                  <label>Catatan (opsional)</label>
                  <textarea value={showApprove.catatan} onChange={e => setShowApprove({...showApprove, catatan: e.target.value})} rows={3} placeholder="Alasan / catatan..." />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowApprove(null)}>Batal</button>
              <button className={`btn ${showApprove.action === 'Disetujui' ? 'btn-success' : 'btn-danger'}`} onClick={handleApprove}>
                {showApprove.action === 'Disetujui' ? 'Setujui' : 'Tolak'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ DETAIL MODAL ============ */}
      {detailItem && (
        <div className="modal-overlay" onClick={() => setDetailItem(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, var(--cyan-500) 0%, var(--cyan-700) 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Calendar size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>
                Detail Penggunaan Cuti
              </h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)',
                border: '1px solid var(--cyan-100)', overflow: 'hidden',
              }}>
                {/* Karyawan Info */}
                <div style={{
                  padding: 16, display: 'flex', alignItems: 'center', gap: 12,
                  borderBottom: '1px solid var(--cyan-100)',
                }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 10,
                    background: 'linear-gradient(135deg, var(--cyan-500), var(--cyan-700))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: '1rem', flexShrink: 0,
                  }}>
                    {(detailItem.nama || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--gray-600)' }}>{detailItem.nama}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>
                      {getNamaNik(detailItem.karyawan_id).split(' - ')[1] || '-'} · {getJabatan(detailItem.karyawan_id)}
                    </div>
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '4px 12px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                      background: (statusConfig[detailItem.status] || statusConfig['Menunggu']).bg,
                      color: (statusConfig[detailItem.status] || statusConfig['Menunggu']).color,
                    }}>
                      <span style={{
                        width: 6, height: 6, borderRadius: '50%',
                        background: (statusConfig[detailItem.status] || statusConfig['Menunggu']).dot,
                      }} />
                      {detailItem.status}
                    </span>
                  </div>
                </div>

                {/* Detail Info Grid */}
                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <DetailField icon={<Calendar size={14} />} label="Jenis Cuti" value={detailItem.jenis_cuti} />
                  <DetailField icon={<Calendar size={14} />} label="Tanggal Mulai" value={detailItem.tgl_mulai} />
                  <DetailField icon={<Calendar size={14} />} label="Tanggal Selesai" value={detailItem.tgl_selesai} />
                  <DetailField icon={<Clock size={14} />} label="Jumlah Hari" value={`${detailItem.total_hari} hari`} />
                  <DetailField icon={<Calendar size={14} />} label="Ganti Libur" value={`${detailItem.ganti_libur || 0} hari`} highlight={detailItem.ganti_libur > 0} />
                  <DetailField icon={<DollarSign size={14} />} label="Total Akhir" value={`${detailItem.total_akhir || (detailItem.total_hari - (detailItem.ganti_libur || 0))} hari`} important />
                  <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--gray-100)', paddingTop: 10 }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)', marginBottom: 4 }}>Alasan</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', fontStyle: 'italic' }}>
                      {detailItem.alasan || '-'}
                    </div>
                  </div>
                  {detailItem.status !== 'Menunggu' && detailItem.approved_name && (
                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--gray-100)', paddingTop: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CheckSquare size={14} style={{ color: detailItem.status === 'Disetujui' ? '#2dce89' : '#f5365c' }} />
                        <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                          {detailItem.status === 'Disetujui' ? 'Disetujui' : 'Ditolak'} oleh <strong>{detailItem.approved_name}</strong>
                          {detailItem.catatan_atasan && <span style={{ fontStyle: 'italic' }}> — "{detailItem.catatan_atasan}"</span>}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDetailItem(null)}>Tutup</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component for detail fields
function DetailField({ icon, label, value, highlight, important }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <div style={{ color: 'var(--gray-400)', marginTop: 2, flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--gray-400)' }}>{label}</div>
        <div style={{
          fontSize: '0.85rem', fontWeight: important ? 700 : 500,
          color: highlight ? '#fb6340' : important ? 'var(--cyan-700)' : 'var(--gray-600)',
        }}>{value}</div>
      </div>
    </div>
  );
}
